import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../axiosConfig';

function readDownloadFilename(contentDisposition) {
    if (!contentDisposition) {
        return 'ISP.docx';

    }

    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch && utfMatch[1]) {
        return decodeURIComponent(utfMatch[1]);
    }

    const basicMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    return (basicMatch && basicMatch[1]) || 'ISP.docx';
}

export default function ISPGenerator({ learnerId, term }) {
    const { id: subjectId } = useParams();
    const [downloading, setDownloading] = useState(false);

    const disabled = useMemo(() => {
        return downloading || !learnerId || !term || !subjectId;
    }, [downloading, learnerId, term, subjectId]);

    const handleDownload = async () => {
        if (!learnerId || !term || !subjectId) {
            window.alert('Missing learner, subject, or term.');
            return;
        }

        setDownloading(true);

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/download_isp.php`, {
                params: {
                    learner_id: learnerId,
                    subject_id: subjectId,
                    term
                },
                responseType: 'blob'
            });

            const filename = readDownloadFilename(response.headers['content-disposition']);
            const blob = new Blob(
                [response.data],
                {
                    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                }
            );

            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);
        } catch (requestError) {
            let message = 'Failed to generate the ISP document.';

            try {
                const errorText = await requestError?.response?.data?.text?.();
                if (errorText) {
                    const parsed = JSON.parse(errorText);
                    message = parsed?.error || message;
                }
            } catch (parseError) {
                message = requestError?.response?.data?.error || requestError?.message || message;
            }

            window.alert(message);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleDownload}
            disabled={disabled}
            title={downloading ? 'Generating ISP...' : 'Generate ISP (Word)'}
            aria-label={downloading ? 'Generating ISP...' : 'Generate ISP (Word)'}
            style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                border: '1px solid #c9d6ff',
                background: downloading ? '#eff4ff' : '#ffffff',
                color: '#2854c5',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.7 : 1,
                transition: 'all 0.2s ease'
            }}
        >
            {downloading ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 2v4"></path>
                    <path d="M17 4.93l-2.83 2.83"></path>
                    <path d="M22 12h-4"></path>
                    <path d="M19.07 17l-2.83-2.83"></path>
                    <path d="M12 22v-4"></path>
                    <path d="M6.93 19.07l2.83-2.83"></path>
                    <path d="M2 12h4"></path>
                    <path d="M4.93 7l2.83 2.83"></path>
                </svg>
            ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <path d="M12 11v6"></path>
                    <path d="M9.5 14.5 12 17l2.5-2.5"></path>
                </svg>
            )}
        </button>
    );
}
